import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useMediaQuery } from '../../_global/jsx/useMediaQuery';

/**
 * An additional width to add to the popup to
 * allow for the bolded text hover/focus styling
 * @const {string}
 */
const POPUP_BOLDED_OFFSET = 10;

/**
 * The number of milliseconds that the popup CSS transition
 * runs for when the popup is closed.
 * This is used to remove the inline styles added by
 * JavaScript when the popup is opened.
 * @const {integer}
 */
const POPUP_TRANSITION_DELAY = 200;

let popupTransitionTimeout = null;

export default function EnhancedSelect({
    selectOptions,
    initialOption,
    componentId,
    selectionCallback,
    allowEmptySelection = true,
}) {
    // Semi-unique ID for this component, used for accessiblity IDs and labels and returned in the optional callback
    // Set or create a new component ID
    const COMPONENT_ID =
        componentId || `enhancedSelect${Math.ceil(Math.random() * 10000)}`;

    /**
     * Respond to keyboard presses within the context of the popup container
     *
     * @param {KeyboardEvent} event Event that triggered the click
     *
     * @returns void
     */
    const onKeyPress = useCallback((event) => {
        if (event.key === 'ArrowUp') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('up');
        }

        if (event.key === 'ArrowDown') {
            // eslint-disable-next-line no-use-before-define
            switchElementFocus('down');
        }

        if (event.key === 'Escape') {
            // Close the popup
            // eslint-disable-next-line no-use-before-define
            togglePopupMenu(false);
        }
    }, []);

    /**
     * Respond to clicks on the hidden popup background
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onBackgroundClick = useCallback((event) => {
        event.stopPropagation();

        // eslint-disable-next-line no-use-before-define
        if (event.target === popupOverlayBackground.current) {
            // Close the popup
            // eslint-disable-next-line no-use-before-define
            togglePopupMenu(false);
        }
    }, []);

    const popupOverlayBackground = useRef(null);
    const openPopupButtonElement = useRef(null);
    const selectPopupElement = useRef(null);
    const selectPopupContentElement = useRef(null);

    const isLargeBreakpointOrGreater = useMediaQuery('(min-width: 1024px)');

    const [popupOptions, setPopupOptions] = useState([]);
    const [popupButtonDimensions, setPopupButtonDimensions] = useState({
        width: 0,
        height: 0,
    });
    const [popupContainerDimensions, setPopupContainerDimensions] = useState({
        width: 0,
        height: 0,
    });
    const [openPopupButtonExpanded, setOpenPopupButtonExpanded] =
        useState(false);
    const [
        openPopupButtonActiveDescendant,
        setOpenPopupButtonActiveDescendant,
    ] = useState('');
    const [
        selectOptionPopupAdditionalClasses,
        setSelectOptionPopupAdditionalClasses,
    ] = useState('');
    const [popupInlineStyles, setPopupInlineStyles] = useState(null);
    const [currentSelectedOption, setCurrentSelectedOption] = useState({
        label: '',
        value: '',
        elementId: '',
    });
    const [focusElementId, setFocusElementId] = useState(null);
    const [enableHiddenOverlay, setEnableHiddenOverlay] = useState(false);

    /**
     * Operations to perform when the component first mounts.
     * These only run once - the first time the component has been added.
     */
    useEffect(() => {
        // Listen for key presses (scoped to the popup)
        selectPopupElement.current.addEventListener(
            'keydown',
            onKeyPress,
            false
        );

        // Listen for clicks (scoped to the hidden overlay background)
        popupOverlayBackground.current.addEventListener(
            'click',
            onBackgroundClick,
            false
        );

        return () => {
            // Remove the listeners when component removed from DOM
            if (selectPopupElement.current !== null) {
                selectPopupElement.current.removeEventListener(
                    'keydown',
                    onKeyPress,
                    false
                );
            }
            if (popupOverlayBackground.current !== null) {
                popupOverlayBackground.current.removeEventListener(
                    'click',
                    onBackgroundClick,
                    false
                );
            }
        };
    }, []);

    /**
     * Listen for changes to the options to use for
     * the Enhanced Select.
     *
     * Update the provided options with an ID and
     * update the local state with the new options.
     */
    useEffect(() => {
        setPopupOptions(
            selectOptions.map((option, index) => {
                return {
                    label: option.label,
                    value: option.value,
                    elementId: `${COMPONENT_ID}-${index}`,
                };
            })
        );
    }, [selectOptions]);

    /**
     * Listen for changes to the media size (tablet or not).
     * Re-calcuate the popup dimensions as the styles change
     * between breakpoints.
     */
    useEffect(() => {
        // Calculate and store the current width and height of the popup button and container
        // eslint-disable-next-line no-use-before-define
        updatePopupContentDimensions();
    }, [isLargeBreakpointOrGreater]);

    /**
     * Operations to perform when the popup options
     * have been populated.
     */
    useEffect(() => {
        // Calculate and store the current width and height of the popup button and container
        // eslint-disable-next-line no-use-before-define
        updatePopupContentDimensions();

        // Set the default value (if any has been provided)
        const initialOptionConfiguration = popupOptions.filter((option) => {
            if (option.value === initialOption.value) {
                return option;
            }
            return false;
        });

        if (initialOption !== null && initialOptionConfiguration.length === 1) {
            setCurrentSelectedOption({
                label: initialOptionConfiguration[0].label,
                value: initialOptionConfiguration[0].value,
                elementId: initialOptionConfiguration[0].elementId,
            });
        }
    }, [popupOptions]);

    /**
     * Operations to perform after a selection has
     * been made
     */
    useEffect(() => {
        // Calculate and store the current width and height of the popup button and container
        // eslint-disable-next-line no-use-before-define
        updatePopupContentDimensions();

        // Pass the selected option to the optional callback
        if (selectionCallback !== null) {
            selectionCallback(currentSelectedOption);
        }
    }, [currentSelectedOption]);

    /**
     * Operations to perform after the focus element has changed
     */
    useEffect(() => {
        let focusDomElement = null;

        // Locate the element in the DOM and set the keyboard focus on it
        if (focusElementId !== '' && focusElementId !== null) {
            // Try to target the provided element
            focusDomElement = document.getElementById(focusElementId);

            if (focusDomElement === null) {
                // Otherwise, set the focus on the first popup option
                focusDomElement = document
                    .getElementById(`${COMPONENT_ID}listbox`)
                    .getElementsByClassName('js-enhanced-select-option')[0];
            }

            focusDomElement.focus();
        }
    }, [focusElementId]);

    /**
     * Calculate the width and height of the open popup
     * button and the popup content and update the
     * local state with the values.
     *
     * @returns void
     */
    function updatePopupContentDimensions() {
        let popupButtonWidth = 0;
        let popupButtonHeight = 0;
        let popupContentWidth = 0;
        let popupContentHeight = 0;
        let popupMaxWidth = 0;

        // Get the current width of the popup button
        popupButtonWidth = Math.ceil(
            openPopupButtonElement.current.getBoundingClientRect().width
        );

        // Get the current height of the popup button
        popupButtonHeight = Math.ceil(
            openPopupButtonElement.current.getBoundingClientRect().height
        );

        /**
         * Set the popup position as initially "fixed" so that
         * JavaScript can calculate the content width correctly
         */
        selectPopupElement.current.style.position = 'fixed';

        popupContentWidth =
            Math.ceil(
                selectPopupElement.current.getBoundingClientRect().width
            ) + POPUP_BOLDED_OFFSET;

        popupContentHeight = Math.ceil(
            selectPopupContentElement.current.getBoundingClientRect().height
        );

        // Check the maximum width allowed for the popup
        popupMaxWidth = parseInt(
            window
                .getComputedStyle(selectPopupElement.current, null)
                .maxWidth.split('px')[0],
            10
        );

        // Remove the inline style change
        selectPopupElement.current.setAttribute('style', '');

        /**
         * If the button is wider than the popup, then increase the
         * popup width to match it
         */
        if (popupButtonWidth > popupContentWidth) {
            popupContentWidth = popupButtonWidth;
        }

        /**
         * Catch any cases where the popup width might be wider
         * than the allowed max-width (as set via CSS)
         */
        if (popupContentWidth > popupMaxWidth) {
            popupContentWidth = popupMaxWidth;
        }

        setPopupButtonDimensions({
            width: popupButtonWidth,
            height: popupButtonHeight,
        });

        setPopupContainerDimensions({
            width: popupContentWidth,
            height: popupContentHeight,
        });
    }

    /**
     * Toggle the open state of the popup menu.
     *
     * Some inline style adjustments need to be made
     * to position the popup correctly.
     *
     * @param {Boolean} setOpen True will open the popup menu
     *
     * @returns void
     */
    function togglePopupMenu(setOpen) {
        // Clear the timeout that removes the popup inline styles
        if (typeof popupTransitionTimeout === 'number') {
            clearTimeout(popupTransitionTimeout);
        }

        if (setOpen) {
            /**
             * Set some inline styles on the popup to position it correctly.
             * Set the popup width.
             * Otherwise it's "position" property will override it's actual width.
             * Center the popup horizontally.
             * Center the popup vertically over the button.
             */
            setPopupInlineStyles({
                top: `-${Math.ceil(
                    popupContainerDimensions.height / 2 -
                        popupButtonDimensions.height / 2
                )}px`,
                left: '50%',
                marginLeft: `-${Math.ceil(
                    popupContainerDimensions.width / 2
                )}px`,
                width: `${popupContainerDimensions.width}px`,
            });

            // Toggle the invisible overlay
            setEnableHiddenOverlay(true);

            // Open the popup
            setSelectOptionPopupAdditionalClasses('is-active');

            // Set the keyboard focus on the currently selected option/button
            setFocusElementId(currentSelectedOption.elementId);
        } else {
            // Toggle the invisible overlay
            setEnableHiddenOverlay(false);

            // Close the popup
            setSelectOptionPopupAdditionalClasses('');

            // Remove the popup inline styles after a short transition delay
            popupTransitionTimeout = setTimeout(() => {
                setPopupInlineStyles(null);
            }, POPUP_TRANSITION_DELAY);

            // Reset some accessibility attributes
            setOpenPopupButtonExpanded(false);
            setOpenPopupButtonActiveDescendant('');
        }
    }

    /**
     * Move the keyboard focus up or down the DOM tree
     *
     * @param {String} direction "up" or "down"
     *
     * @returns void
     */
    function switchElementFocus(direction) {
        // Get the current element in focus
        const currentFocusElement = document.activeElement;
        let switchOptionParent = null;

        // If it's an option from our popup then switch focus to the next option in the list
        if (
            currentFocusElement.className.indexOf(
                'js-enhanced-select-option'
            ) !== -1
        ) {
            if (direction === 'up') {
                // Find the previous element in the list
                switchOptionParent =
                    currentFocusElement.parentNode.previousElementSibling;
            } else {
                // Find the next element in the list
                switchOptionParent =
                    currentFocusElement.parentNode.nextElementSibling;
            }

            // We don't loop back around, we stop at the last option in the list
            if (switchOptionParent !== null) {
                switchOptionParent
                    .getElementsByClassName('js-enhanced-select-option')[0]
                    .focus();
            }
        }
    }

    /**
     * Respond to clicks on the button that opens the popup menu.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onSelectOpenClick = (evt) => {
        evt.preventDefault();

        // Set some accessibility attributes
        setOpenPopupButtonExpanded(true);

        setOpenPopupButtonActiveDescendant(currentSelectedOption.elementId);

        // Open the popup
        togglePopupMenu(true);
    };

    /**
     * Respond to clicks on the option button(s).
     * Capture the new value and close the popup.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onSelectOptionClick = (evt) => {
        const selectedValue =
            evt.currentTarget.getAttribute('data-option-value');

        evt.preventDefault();

        // Lookup the ID and label of the selected option
        const selectedOptionData = popupOptions.filter((option) => {
            if (option.value === selectedValue) {
                return option;
            }
            return false;
        });

        // Close the popup
        togglePopupMenu(false);

        // Update the state with the selected option
        // Check if the selection option is different to the current one
        if (
            currentSelectedOption.elementId !== selectedOptionData[0].elementId
        ) {
            setCurrentSelectedOption({
                label: selectedOptionData[0].label,
                value: selectedOptionData[0].value,
                elementId: selectedOptionData[0].elementId,
            });
        }
    };

    return (
        <>
            <div
                className={`enhanced-select__overlay-background ${
                    enableHiddenOverlay ? 'is-active' : ''
                }`}
                ref={popupOverlayBackground}
            />
            <div className="enhanced-select__container no-wysiwyg">
                <span className="sr-only">
                    Click the following button to make a new selection. Your current selection is:
                </span>
                <button
                    aria-controls={`${COMPONENT_ID}listbox`}
                    aria-expanded={openPopupButtonExpanded}
                    aria-activedescendant={openPopupButtonActiveDescendant}
                    aria-haspopup="listbox"
                    className="enhanced-select__button"
                    onClick={onSelectOpenClick}
                    ref={openPopupButtonElement}
                    role="combobox"
                    tabIndex="0"
                    type="button"
                >
                    <span className="enhanced-select__button-label">{currentSelectedOption.label}</span>
                    <svg
                        className="svg-icon"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M22.0001 16L12.0001 26L10.6001 24.6L19.2001 16L10.6001 7.4L12.0001 6L22.0001 16Z"
                            fill="currentColor"
                        />
                    </svg>
                </button>
                <div
                    className={`enhanced-select-popup__container ${selectOptionPopupAdditionalClasses}`}
                    id={`${COMPONENT_ID}listbox`}
                    ref={selectPopupElement}
                    role="listbox"
                    style={popupInlineStyles}
                    tabIndex="-1"
                >
                    <span className="sr-only">
                        Press the Escape key to close this popup.
                    </span>
                    <div
                        className="enhanced-select-popup__list-wrapper"
                        ref={selectPopupContentElement}
                    >
                        <div className="enhanced-select-popup__list-scroller">
                            <ul className="enhanced-select-popup__list">
                                {popupOptions.map((option) => {
                                    const optionIsSelected =
                                        option.value ===
                                        currentSelectedOption.value;

                                    let additionalButtonClasses = '';

                                    if (optionIsSelected) {
                                        additionalButtonClasses = 'selected';
                                    }

                                    if (
                                        !allowEmptySelection &&
                                        option.value === ''
                                    ) {
                                        return null;
                                    }

                                    return (
                                        <li
                                            className="enhanced-select-popup__list-item"
                                            key={option.elementId}
                                        >
                                            <button
                                                aria-selected={optionIsSelected}
                                                className={`js-enhanced-select-option enhanced-select-popup__option ${additionalButtonClasses}`}
                                                data-option-value={option.value}
                                                id={option.elementId}
                                                onClick={onSelectOptionClick}
                                                role="option"
                                                type="button"
                                            >
                                                {option.label}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

EnhancedSelect.propTypes = {
    selectOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        })
    ),
    initialOption: PropTypes.shape({
        value: PropTypes.string,
    }),
    componentId: PropTypes.string,
    selectionCallback: PropTypes.func,
    allowEmptySelection: PropTypes.bool,
};

EnhancedSelect.defaultProps = {
    selectOptions: [
        {
            label: 'Select an option',
            value: '',
        },
        {
            label: 'Early Childhood Educator',
            value: 'ece',
        },
        {
            label: 'Malesuada Ipsum Vulputate Cursus',
            value: 'mivc',
        },
        {
            label: 'XYZ',
            value: 'xyz',
        },
        {
            label: 'School Leader',
            value: 'sl',
        },
        {
            label: 'Year Level Coordinator',
            value: 'ylc',
        },
        {
            label: 'Subject Coordinator',
            value: 'sc',
        },
        {
            label: 'Learner/Novice Driver',
            value: 'lnd',
        },
    ],
    initialOption: {
        value: 'sl',
    },
    componentId: null,
    selectionCallback: null,
    allowEmptySelection: true,
};
